import React, { useMemo } from "react";
import { StyleSheet } from "react-native";
import Animated, { useAnimatedStyle, SharedValue } from "react-native-reanimated";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";

export interface SummaryCellProps {
  columnKey: string;
  value: any;
  columnWidths: SharedValue<Record<string, number>>;
  savedWidth?: number;
  minCellWidth?: number;
  alignment?: "left" | "right" | "center";
}

export function SummaryCell({
  columnKey,
  value,
  columnWidths,
  savedWidth = 150,
  minCellWidth = 100,
  alignment = "left",
}: SummaryCellProps) {
  const { colors } = useTheme();

  const animatedStyle = useAnimatedStyle(() => {
    return { width: columnWidths.value[columnKey] ?? savedWidth };
  }, []);

  // Format display value
  const displayValue = useMemo(() => {
    if (value === null || value === undefined) return "";
    if (typeof value === "number") {
      // Check if it looks like currency (has decimals)
      if (value % 1 !== 0) {
        return `$${value.toFixed(2)}`;
      }
      return value.toString();
    }
    return value.toString();
  }, [value]);

  const textAlign = alignment === "left" ? "left" : alignment === "right" ? "right" : "center";

  return (
    <Animated.View
      style={[
        styles.container,
        animatedStyle,
        {
          borderColor: colors.border,
          backgroundColor: colors.backgroundSecondary,
          minWidth: minCellWidth,
        },
      ]}
    >
      <ThemedText
        numberOfLines={1}
        style={[
          styles.text,
          {
            color: colors.text,
            textAlign,
            fontWeight: "600",
          },
        ]}
      >
        {displayValue}
      </ThemedText>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    height: 36,
    paddingHorizontal: 8,
    justifyContent: "center",
    borderRightWidth: StyleSheet.hairlineWidth,
  },
  text: {
    fontSize: 16,
  },
});
